/**
 * 수수료/리워드 서비스
 * - 수수료 생성, 부동산별 조회, 합계 계산
 */
import { db } from '../firebase/config';
import {
  collection, doc, getDoc, getDocs, addDoc,
  query, where, orderBy, serverTimestamp,
} from 'firebase/firestore';
import { calcCompletionPrice } from './partnerAssignmentService';
import { getSiteSettings } from './settingsService';

// 신청 폼 moving_type 값 → DbPricing 키 매핑 (full_pack → pack 등)
const normalizeMovingType = (key) => {
  if (!key) return 'pack';
  const k = String(key).toLowerCase();
  if (['pack', 'full_pack', 'package'].includes(k)) return 'pack';
  if (['semi_pack', 'half_pack', 'semi', 'semi_package'].includes(k)) return 'semi_pack';
  if (['van', 'cargo'].includes(k)) return 'van';
  if (['normal', 'simple', 'ordinary', 'general', 'regular'].includes(k)) return 'normal';
  return k;
};

const COMM_COL = 'commissions';
const USER_COL = 'users';

// ───── 수수료 금액 계산 유틸 ─────
// 담당자 확정 정책 (2026-04-24):
//  - 전환수수료 = 완료가 × industryDefaults[category].completeRate% (기본 35%)
//  - 추천수수료 = 전환수수료 × referralPolicy.rate% (기본 5%, 1년 이내 추천)
const DEFAULT_CONVERSION_RATE = 0.35;
const DEFAULT_REFERRAL_RATE = 0.05;

/**
 * 완료가 기반 전환수수료 계산.
 * - settings.industryDefaults[category].completeRate 우선, 없으면 기본 35%
 */
const calcConversionAmount = async (category, completionPrice) => {
  const price = Number(completionPrice) || 0;
  if (price <= 0) return 0;

  let rate = DEFAULT_CONVERSION_RATE;
  try {
    const data = await getSiteSettings();
    const industries = Array.isArray(data?.industryDefaults) ? data.industryDefaults : [];
    const found = industries.find((x) => x.key === category);
    if (found && typeof found.completeRate === 'number') {
      rate = found.completeRate / 100;
    } else if (typeof data?.commissionRates?.[category] === 'number') {
      rate = data.commissionRates[category]; // 구 구조 호환
    }
  } catch { /* fallback */ }

  return Math.floor(price * rate);
};

// referralPolicy.rate (%) 로 referral rate
const getReferralRate = async () => {
  try {
    const data = await getSiteSettings();
    const r = data?.referralPolicy?.rate;
    if (typeof r === 'number') return r / 100;
  } catch { /* fallback */ }
  return DEFAULT_REFERRAL_RATE;
};

// ───── 상담수수료 생성 (상담중 진입 시) ─────
// 부동산 유입 건에 한해서, 상담 시작되면 고정 상담수수료를 부동산에 지급.
// 금액: settings.movingConsult[movingType] > settings.industryDefaults[category].consult
export const createConsultingCommission = async (serviceRequest) => {
  const { source_realtor_id, category, id: requestId } = serviceRequest;
  if (!source_realtor_id) return null;

  // 중복 방지
  const existing = await getDocs(query(
    collection(db, COMM_COL),
    where('service_request_id', '==', requestId),
    where('type', '==', 'consulting'),
  ));
  if (!existing.empty) return null;

  let amount = 0;
  try {
    const settings = await getSiteSettings();

    // 이사: 타입별(movingConsult) 우선 — 키 정규화 (full_pack → pack 등)
    if (category === 'moving') {
      const mType = normalizeMovingType(serviceRequest.moving_type);
      amount = Number(settings.movingConsult?.[mType]) || 0;
    }
    // fallback: industryDefaults[category].consult
    if (!amount) {
      const industries = Array.isArray(settings.industryDefaults) ? settings.industryDefaults : [];
      const found = industries.find((x) => x.key === category);
      amount = Number(found?.consult) || 0;
    }
  } catch { /* fallback */ }

  if (amount <= 0) return null;

  const commRef = await addDoc(collection(db, COMM_COL), {
    realtor_id: source_realtor_id,
    service_request_id: requestId,
    type: 'consulting',
    category,
    amount,
    is_settled: false,
    paid_by_partner: false,
    created_at: serverTimestamp(),
  });
  return commRef.id;
};

// ───── 수수료 생성 (서비스 완료 시) ─────
export const createCommission = async (serviceRequest, assignmentData = {}) => {
  const { source_realtor_id, category, id: requestId } = serviceRequest;
  if (!source_realtor_id) return null;

  // 1) 수수료 테이블 기반 완료가 계산
  const movingType = serviceRequest.moving_type || assignmentData.moving_type || '';
  const internetType = serviceRequest.internet_type || assignmentData.internet_type || '';
  const pyeong = Number(serviceRequest.area_pyeong_exact) || 25;
  const completionPrice = await calcCompletionPrice(category, { movingType, pyeong, internetType });

  // 2) 전환수수료 = 완료가 × completeRate%
  const conversionAmount = await calcConversionAmount(category, completionPrice);

  const commRef = await addDoc(collection(db, COMM_COL), {
    realtor_id: source_realtor_id,
    service_request_id: requestId,
    type: 'conversion',
    category,
    completion_price: completionPrice,
    amount: conversionAmount,
    is_settled: false,
    paid_by_partner: false,
    created_at: serverTimestamp(),
  });

  // 3) 추천수수료 (1년 이내 추천인)
  try {
    const realtorSnap = await getDoc(doc(db, USER_COL, source_realtor_id));
    if (realtorSnap.exists()) {
      const referrerId = realtorSnap.data().referrer_id;
      if (referrerId) {
        const referredAt = realtorSnap.data().created_at?.toDate?.();
        const oneYearAgo = new Date();
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

        if (referredAt && referredAt > oneYearAgo) {
          const referralRate = await getReferralRate();
          const referralAmount = Math.floor(conversionAmount * referralRate);
          await addDoc(collection(db, COMM_COL), {
            realtor_id: referrerId,
            referred_realtor_id: source_realtor_id,
            service_request_id: requestId,
            type: 'referral',
            category,
            completion_price: completionPrice,
            amount: referralAmount,
            is_settled: false,
            paid_by_partner: false,
            created_at: serverTimestamp(),
          });
        }
      }
    }
  } catch { /* skip */ }

  return commRef.id;
};

// ───── 부동산별 수수료 조회 ─────
// Firestore 컴포지트 인덱스 회피: where만 쓰고 클라이언트 측 정렬
export const getCommissionsByRealtor = async (realtorId) => {
  const q = query(collection(db, COMM_COL), where('realtor_id', '==', realtorId));
  const snap = await getDocs(q);
  const list = snap.docs.map((d) => ({
    id: d.id,
    ...d.data(),
    createdAt: d.data().created_at?.toDate?.()?.toISOString().slice(0, 10) || '',
  }));
  return list.sort((a, b) => {
    const aT = a.created_at?.seconds || 0;
    const bT = b.created_at?.seconds || 0;
    return bT - aT;
  });
};

// ───── 부동산별 수수료 합계 ─────
export const getCommissionSummary = async (realtorId) => {
  const commissions = await getCommissionsByRealtor(realtorId);

  const summary = {
    total: 0,
    settled: 0,
    withdrawable: 0,
    conversion: 0,
    referral: 0,
    consulting: 0,
    count: commissions.length,
    unsettledCount: 0,
  };

  commissions.forEach((c) => {
    const amt = c.amount || 0;
    summary.total += amt;
    if (c.is_settled) {
      summary.settled += amt;
    } else {
      summary.unsettledCount += 1;
    }
    if (c.type === 'conversion') summary.conversion += amt;
    else if (c.type === 'referral') summary.referral += amt;
    else if (c.type === 'consulting') summary.consulting += amt;
  });

  summary.withdrawable = summary.settled;

  return summary;
};
